import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TasksConfig } from "./config";
import {
  AlertDetails,
  ApplyTaskFilters,
  Tasks,
} from "src/app/common/interfaces/home/tasks.interface";
import { StorageService } from "src/app/common/services/storage.service";
import { LandingClientService } from "src/app/common/services/landing-page.client";
import { LandingPageService } from "../services/landing-page.service";
import {
  SidePanelDetails,
  PanelData,
} from "src/app/common/interfaces/home/side-panel-details.interface";
import { delay, distinctUntilChanged, firstValueFrom, Subscription } from "rxjs";
import { Alerts, getAlertType } from "src/app/common/enums/Tasks.enum";
import { AppConstants } from "src/app/common/constants/app-constants";
import { ViewTypeService } from "src/app/services/view-type.service";
import { ViewType } from "src/app/common/enums/view-type.enum";
import { LandingPageTaskService } from "src/app/common/services/landingPage-task.service";
import {
  isReactDocumentationEnabled,
  reactDocumentationUrl,
} from "src/app/common/utils/react-documentation-route.util";
import { TherapyReferralEmbedConstants } from "../side-panel/therapy-referral-embed/therapy-referral-embed.constants";

@Component({
  selector: "app-tasks",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./tasks.component.html",
  styleUrls: ["./tasks.component.scss"],
})
export class TasksComponent implements OnInit {
  tasks: any[] = [];
  tasksDetails!: Tasks[];
  facilityId!: string | null;
  private subscriptions: Subscription = new Subscription();
  selectedViewType: ViewType = ViewType.DOR_VIEW;
  private facilitySubscription: Subscription | null = null;
  requiredTherapistFilter: String[] = [Alerts.MissedTimeSheets];
  requiredPatientFilter: String[] = [Alerts.OverDueDocuments, Alerts.MissedVisits];
  requiredDisciplineFilter: String[] = [Alerts.OverDueDocuments, Alerts.MissedVisits];
  requiredPhysicianFilter: String[] = [Alerts.MissedMDSignatures];
  alertsFilters: String[] = [Alerts.OverDueDocuments, Alerts.MissedVisits, Alerts.MissedTimeSheets, Alerts.MissedMDSignatures]

  constructor(
    public storageService: StorageService,
    private landingClientService: LandingClientService,
    private landingPageService: LandingPageService,
    private viewTypeService: ViewTypeService,
    private landingPageTaskService: LandingPageTaskService
  ) {}

  async ngOnInit() {
    this.tasks = TasksConfig;
    this.facilityId = this.storageService.getSelectedFacilityId();
    const viewTypeSub = this.viewTypeService.selectedViewType$
      .pipe(distinctUntilChanged())
      .subscribe((viewType) => {
        this.selectedViewType = viewType;
        this.facilitySubscription?.unsubscribe();
         this.subscriptions?.unsubscribe();
         this.subscriptions = new Subscription();
        if (viewType !== ViewType.NONE) {
          this.facilitySubscription = this.storageService.isSelectedFacility
            .pipe(distinctUntilChanged(),delay(100))
            .subscribe((facilityId) => {
              this.facilityId = facilityId;
              this.getTasks();
            });
          this.subscriptions.add(this.facilitySubscription);
          const taskDetailsSub = this.landingPageTaskService.tasksDetails.subscribe((tasks) => {
            if (tasks){
              this.tasksDetails = tasks;
              if(this.landingPageService.getIsToggleSidePanel()) {
                const taskName = this.landingPageTaskService.getTaskName();
                this.landingPageService.setSidePanelData(this.preparePanelDetails(taskName));
              }
            }
          });

          this.subscriptions.add(taskDetailsSub);
        }
      });

    this.subscriptions.add(viewTypeSub);
  }
  
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.facilitySubscription?.unsubscribe();
    this.landingPageService.toggleSidePanel(false);
  }

  async getTasks() {
    this.tasksDetails = await firstValueFrom(
      this.landingClientService.getTasks(this.facilityId, this.selectedViewType, true)
    );
    this.tasksDetails = this.tasksDetails.map(item => {
      if(!!item)
        {
          return {
            ...item,
            alertFilters : undefined
          }
        }
        return item;
      });
    this.landingPageTaskService.setTasksDetails(this.tasksDetails);
  }

  preparePanelDetails(taskName: string): SidePanelDetails {
    if (taskName.toLowerCase() === Alerts.TherapyScreenOrReferral.toLowerCase()) {
      return {
        title: TherapyReferralEmbedConstants.panelTitle,
        panelDetails: [],
        isTherapyReferralEmbed: true,
      };
    }

    const taskDetail = this.tasksDetails?.find(
      (item) => item?.title.toLowerCase() === taskName.toLowerCase()
    );
    const data: PanelData[] =
      taskDetail?.alertDetails?.map((alertDetail) => ({
       ...alertDetail,
        route: this.getRouteForTask(taskName, alertDetail) ?? undefined,
      })) ?? [];

    const alertKey = Object.keys(Alerts).find(
        key => Alerts[key as keyof typeof Alerts] === taskName
      ) as keyof typeof Alerts | undefined;

    const alertType = alertKey ? getAlertType(Alerts[alertKey]) : undefined;
    return {
      title: `${taskName}`,
      panelDetails: data,
      discrepancies: taskDetail?.discrepancies,
      signaturedata: taskDetail?.signaturedata,
      filterDetails: this.alertsFilters.includes(taskName) ? {
        isTherapistFilter: this.requiredTherapistFilter.includes(taskName) && this.selectedViewType != ViewType.THERAPIST_VIEW,
        isPatientFilter: this.requiredPatientFilter.includes(taskName),
        isDisciplineFilter: this.requiredDisciplineFilter.includes(taskName),
        isPhysicianFilter: this.requiredPhysicianFilter.includes(taskName)
      } : undefined,
      alertType: alertType,
      alertFilters: taskDetail?.alertFilters,
      headerSection: taskDetail?.alertDetailsExpectedFields,
      footerMessage: taskName === Alerts.TimeCardImportFailures
        ? AppConstants.timeCardImportFailuresFooterMessage
        : null,
      infoNotes: taskName === Alerts.TimeCardImportFailures
        ? AppConstants.timeCardImportFailuresInfoNotes
        : null,
    };
  }

  isValidDate(date: string): string {
    return date === AppConstants.defaultDate ? AppConstants.emDash : date;
  }

  getRouteForTask(taskName: string, alertDetail: AlertDetails): string | null {
    switch (taskName.toLowerCase()) {
      case Alerts.OverDueDocuments.toLowerCase():
        // The side panel hard-navigates any `/rc/` route out of Angular.
        if (isReactDocumentationEnabled() && alertDetail.patientId) {
          return reactDocumentationUrl(
            this.facilityId,
            alertDetail.patientId,
            alertDetail.discipline,
          );
        }
        return `/patient/${this.facilityId}/profile/${alertDetail.patientId}/document/${alertDetail.discipline}`;
      case Alerts.MissedTimeSheets.toLowerCase():
        return `/time-card?date=${alertDetail.date}&user=${alertDetail.therapistId}`;
      case Alerts.TimeCardImportFailures.toLowerCase():
        return alertDetail.therapistId ? `/user/${alertDetail.therapistId}/positions` : null;
      default:
        return null;
    }
  }

  getSubHeaderTitle(taskName: string): string {
    switch (taskName.toLowerCase()) {
      case Alerts.MissedTimeSheets.toLowerCase():
        return `Time Card Date: `;
      case Alerts.NoTherapyThreeDays.toLowerCase():
        return `Last Treatment: `;
      default:
        return "";
    }
  }

  async openSidePanel(taskName: string): Promise<void> {
    this.landingPageTaskService.setTaskName(taskName);

    if (taskName === Alerts.TimeCardImportFailures && this.facilityId) {
      const alertType = getAlertType(Alerts.TimeCardImportFailures);
      const filters: ApplyTaskFilters = {
        facilityId: this.facilityId,
        viewType: this.selectedViewType,
        alertType,
      };
      const freshDetails = await firstValueFrom(
        this.landingClientService.getFilteredAlertData(filters)
      );
      this.landingPageTaskService.updateTasksDetailsValue(freshDetails, alertType);
      this.tasksDetails = this.landingPageTaskService.getTasksDetails();
    }

    const data = this.preparePanelDetails(taskName);
    this.landingPageService.setSidePanelData(data);
    this.landingPageService.toggleSidePanel(true);
  }

  getCount(taskName: string): string {
    const count = this.getTaskCount(taskName);
    return count < 10 ? "0" + count : count.toString();
  }

  isTaskHidden(task: { name: string; hideWhenZero?: boolean }): boolean {
    return !!task.hideWhenZero && this.getTaskCount(task.name) === 0;
  }

   private getTaskCount(taskName: string): number {
    return (
      this.tasksDetails?.find(
        (item) => item?.title.toLowerCase() === taskName.toLowerCase()
      )?.count ?? 0
    );
  }
  isTherapyReferralTask(taskName: string): boolean {
    return taskName.toLowerCase() === Alerts.TherapyScreenOrReferral.toLowerCase();
  }

  getDisplayName(taskName: string): string {
    return this.isTherapyReferralTask(taskName)
      ? TherapyReferralEmbedConstants.panelTitle
      : taskName;
  }

}
